import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import PerfectScrollbar from "react-perfect-scrollbar";
import { makeStyles } from "@material-ui/styles";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
} from "@material-ui/core";
import { axiosInstance } from "common/network";
import { StatusBullet } from "components";
import { downloadDidimoFormat } from "services/didimoDownload";
import DidimoDetails from "../DidimoDetails";

const PAGE_SIZE = 5;

// Newest first. The API takes order_by with a leading "-" for descending.
const ORDER_BY = "-created_at";

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: "white",
    marginTop: "28px",
    minHeight: "200px",
  },
  content: {
    padding: 0,
  },
  inner: {
    minWidth: 900,
  },
  actions: {
    justifyContent: "flex-end",
  },
  statusContainer: {
    display: "flex",
    alignItems: "center",
    textTransform: "capitalize",
  },
  status: {
    marginRight: theme.spacing(1),
  },
  key: {
    fontFamily: "monospace",
  },
  notAvailable: {
    color: "rgba(0,0,0,0.4)",
    fontStyle: "italic",
  },
  empty: {
    padding: theme.spacing(3),
  },
  formats: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  formatButton: {
    textTransform: "none",
    minWidth: 0,
  },
}));

// Maps a didimo status to a StatusBullet colour. Unknown statuses fall back to neutral.
const statusColors = {
  done: "success",
  completed: "success",
  processing: "info",
  pending: "info",
  in_progress: "info",
  error: "danger",
  deleted: "danger",
};

const AccountDidimos = ({ accountUuid }) => {
  const classes = useStyles();
  const [didimos, setDidimos] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // 0-based for TablePagination; the API is 1-based.
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  // Keys "<didimoKey>:<format>" currently downloading, to show progress and avoid double clicks.
  const [downloading, setDownloading] = useState({});
  // The didimo whose details modal is open (null = closed).
  const [selected, setSelected] = useState(null);

  const fetchDidimos = useCallback(async () => {
    if (!accountUuid) return;
    setLoading(true);
    setError(false);
    try {
      const response = await axiosInstance.get(
        process.env.REACT_APP_API_ROOT + `v3/accounts/${accountUuid}/didimos`,
        {
          // page is 1-based on the API. order_by="-created_at" lists newest first.
          // The `key` filter (with `*` wildcard), `status`, `dgp` and date ranges
          // are supported here too — that is where a future search UI will plug in.
          params: { page: page + 1, page_size: PAGE_SIZE, order_by: ORDER_BY },
        }
      );
      setDidimos(response?.data?.didimos || []);
      setTotal(response?.data?.total_size || 0);
    } catch (err) {
      console.log("fetchDidimos:", err);
      setError(true);
      setDidimos([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [accountUuid, page]);

  useEffect(() => {
    fetchDidimos();
  }, [fetchDidimos]);

  // A new account resets us to the first page.
  useEffect(() => {
    setPage(0);
  }, [accountUuid]);

  // Downloads one transfer format (shared helper handles the presigned-URL dance).
  const downloadFormat = async (didimoKey, format) => {
    if (!format?.__links?.self) return;
    const marker = `${didimoKey}:${format.name}`;
    setDownloading((d) => ({ ...d, [marker]: true }));
    try {
      await downloadDidimoFormat(format);
    } catch (err) {
      console.log("downloadFormat:", err);
      alert(`Could not download "${format.name}". Please try again.`);
    } finally {
      setDownloading((d) => {
        const next = { ...d };
        delete next[marker];
        return next;
      });
    }
  };

  return (
    <>
    <Card className={classes.root}>
      <CardHeader title="Didimos" subheader={total > 0 ? `${total} total` : undefined} />
      <CardContent className={classes.content}>
        <PerfectScrollbar>
          <div className={classes.inner}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Didimo</TableCell>
                  <TableCell>Input type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Downloads</TableCell>
                </TableRow>
              </TableHead>
              <TableBody className={loading ? "loading" : ""}>
                {didimos.map((didimo) => {
                  const formats = didimo.transfer_formats || [];
                  return (
                    <TableRow hover key={didimo.key}>
                      <TableCell className={classes.key}>
                        <Link
                          component="button"
                          variant="body2"
                          className={classes.key}
                          onClick={() => setSelected(didimo)}
                        >
                          {didimo.key}
                        </Link>
                      </TableCell>
                      <TableCell>{didimo.input_type || "—"}</TableCell>
                      <TableCell>
                        <div className={classes.statusContainer}>
                          <StatusBullet
                            className={classes.status}
                            color={statusColors[didimo.status] || "neutral"}
                            size="sm"
                          />
                          {didimo.status}
                        </div>
                      </TableCell>
                      <TableCell>
                        {didimo.percent != null ? `${didimo.percent}%` : "—"}
                      </TableCell>
                      <TableCell>
                        {didimo.created_at
                          ? moment(didimo.created_at).format("DD/MM/YYYY HH:mm")
                          : "—"}
                      </TableCell>
                      <TableCell>
                        {didimo.expires_at
                          ? moment(didimo.expires_at).format("DD/MM/YYYY")
                          : "—"}
                      </TableCell>
                      <TableCell>
                        {formats.length > 0 ? (
                          <div className={classes.formats}>
                            {formats.map((format) => {
                              const marker = `${didimo.key}:${format.name}`;
                              const busy = !!downloading[marker];
                              return (
                                <Button
                                  key={format.name}
                                  className={classes.formatButton}
                                  variant="outlined"
                                  color="primary"
                                  size="small"
                                  disabled={busy}
                                  onClick={() => downloadFormat(didimo.key, format)}
                                  startIcon={
                                    busy ? <CircularProgress size={14} /> : undefined
                                  }
                                >
                                  {format.name}
                                </Button>
                              );
                            })}
                          </div>
                        ) : (
                          <Typography className={classes.notAvailable}>—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {!loading && didimos.length === 0 && (
              <Typography className={classes.empty}>
                {error ? "Could not load didimos." : "This account has no didimos."}
              </Typography>
            )}
          </div>
        </PerfectScrollbar>
      </CardContent>
      {total > 0 && (
        <CardActions className={classes.actions}>
          <TablePagination
            component="div"
            count={total}
            onChangePage={(event, newPage) => setPage(newPage)}
            onChangeRowsPerPage={() => {}}
            page={page}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
            showFirstButton={true}
            showLastButton={true}
          />
        </CardActions>
      )}
    </Card>
    <DidimoDetails
      didimo={selected}
      open={!!selected}
      onClose={() => setSelected(null)}
    />
    </>
  );
};

AccountDidimos.propTypes = {
  accountUuid: PropTypes.string,
};

export default AccountDidimos;
